import React, { useState } from 'react';
import { MoreHorizontal, MessageCircle, Heart, ThumbsUp, Award, Flag, Copy, ChevronDown, ChevronUp } from 'lucide-react';
import { Comment } from '../../types';
import { showToast } from '../../utils/toast';

interface ReplyCardProps {
  comment: Comment;
  isExpanded: boolean;
  onToggleExpand: () => void;
  onReply: (commentId: string) => void;
  onReaction: (commentId: string, reaction: string) => void;
  onViewProfile: (userId: string) => void;
  isNested?: boolean;
  highlightStyle?: string;
}

const ReplyCard: React.FC<ReplyCardProps> = ({
  comment,
  isExpanded,
  onToggleExpand,
  onReply,
  onReaction,
  onViewProfile,
  isNested = false,
  highlightStyle = ''
}) => {
  const [showOptions, setShowOptions] = useState(false);
  
  // Determine if content needs to be truncated
  const needsTruncation = comment.content.length > 300;
  const displayContent = !isExpanded && needsTruncation 
    ? comment.content.substring(0, 300) + '...' 
    : comment.content;
  
  const handleCopyLink = () => {
    // In a real app, this would copy a direct link to the comment
    navigator.clipboard.writeText(`https://example.com/community/post/${comment.postId}?comment=${comment.id}`);
    showToast.system.copied('Comment link');
    setShowOptions(false);
  };
  
  const handleReportComment = () => {
    showToast.community.postReported();
    setShowOptions(false);
  };
  
  return (
    <div className={`p-4 rounded-xl border transition-all duration-200 ${
      comment.isExpertAnswer 
        ? 'bg-green-50 dark:bg-green-900/30 border-green-200 dark:border-green-800' 
        : highlightStyle || 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700'
    } ${isNested ? 'hover:shadow-sm' : 'hover:shadow-md'}`}>
      <div className="flex items-start space-x-3">
        {/* Avatar */}
        <button
          onClick={() => onViewProfile(comment.authorId)}
          className="avatar-sm hover:scale-110 transition-transform duration-200"
        >
          {comment.authorName.charAt(0)}
        </button>
        
        {/* Content */}
        <div className="flex-1">
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center space-x-2">
              <button
                onClick={() => onViewProfile(comment.authorId)}
                className="font-medium text-gray-900 dark:text-gray-100 text-sm hover:text-green-600 dark:hover:text-green-400 transition-colors duration-200"
              >
                {comment.authorName}
              </button>
              {comment.isExpert && (
                <span className="badge-success animate-pulse flex items-center space-x-1">
                  <Award size={12} />
                  <span>Expert</span>
                </span>
              )}
              {comment.isExpertAnswer && (
                <span className="badge-info text-xs">Expert Answer</span>
              )}
              <span className="text-gray-500 dark:text-gray-400 text-xs">{comment.timestamp}</span>
            </div>
            
            <div className="relative">
              <button
                onClick={() => setShowOptions(!showOptions)}
                className="p-1 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                <MoreHorizontal size={16} />
              </button>
              
              {showOptions && (
                <div className="absolute right-0 mt-1 w-40 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-10 animate-scale-in">
                  <div className="py-1">
                    <button
                      onClick={handleReportComment}
                      className="flex items-center space-x-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 w-full text-left"
                    >
                      <Flag size={14} />
                      <span>Report</span>
                    </button>
                    <button
                      onClick={handleCopyLink}
                      className="flex items-center space-x-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 w-full text-left"
                    >
                      <Copy size={14} />
                      <span>Copy Link</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
          
          {/* Comment Content */}
          <div className="text-gray-800 dark:text-gray-200 text-sm whitespace-pre-line">
            {displayContent}
            {needsTruncation && (
              <button
                onClick={onToggleExpand}
                className="block mt-1 text-green-600 dark:text-green-400 hover:text-green-700 dark:hover:text-green-300 text-xs font-medium"
              >
                {isExpanded ? 'Show less' : 'Read more'}
              </button>
            )}
          </div>
          
          {/* Reaction Bar */}
          <div className="flex items-center space-x-3 mt-3">
            <button
              onClick={() => onReaction(comment.id, 'helpful')}
              className="flex items-center space-x-1 text-gray-500 dark:text-gray-400 hover:text-green-600 dark:hover:text-green-400 text-xs transition-colors"
            >
              <ThumbsUp size={14} />
              <span>Helpful</span>
              {comment.reactions?.filter(r => r.type === 'helpful').length > 0 && (
                <span className="font-medium">{comment.reactions.filter(r => r.type === 'helpful').length}</span>
              )}
            </button>
            <button
              onClick={() => onReaction(comment.id, 'heart')}
              className="flex items-center space-x-1 text-gray-500 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 text-xs transition-colors"
            >
              <Heart size={14} />
              <span>Heart</span>
              {comment.reactions?.filter(r => r.type === 'heart').length > 0 && (
                <span className="font-medium">{comment.reactions.filter(r => r.type === 'heart').length}</span>
              )}
            </button>
            <button
              onClick={() => onReply(comment.id)}
              className="flex items-center space-x-1 text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 text-xs transition-colors"
            >
              <MessageCircle size={14} />
              <span>Reply</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReplyCard;